import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import Select from '@/components/@IntranetLibrary/Select';
import { Caption } from '@/components/@IntranetLibrary/Typography';
import { approvalTableCellSelectSx } from '@/components/Approval/@components/ApprovalFormTable';
import {
  BudgetSummaryTable,
  TotalAmountTable,
} from '@/components/Approval/@components/EntExpense';
import {
  APPROVAL_SECTION_GAP,
  APPROVAL_SECTION_GAP_LG,
  InfoSectionTitle,
} from '@/components/Approval/@components/InfoSection';
import { AiFileUploader } from '@/components/Common/FileUpload';
import {
  getApprovalAiDraftSourceUrl,
  getSapCostCenterUrl,
  SAP_ENTERTAINMENT_BUDGET_URL,
} from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import ExclamationCircleIcon from '@/public/icons/approval/exclamation-circle.svg';
import type { AttachmentUploadResult } from '@/types/approval';
import { fetchApi } from '@/utils/api/customAxios';
import {
  applyEntAiResult,
  computeEntTotalAmount,
  createEntExpenseState,
  ENT_BUDGET_SEASON,
  ENT_EXPENSE_TYPE,
  ENT_SAP_COMPANY_CODE,
  getBudgetYearOptions,
  parseEntAiResult,
  syncRowsToBudget,
  toCostCenterOptions,
  toEntBudget,
  type EntCostCenterOption,
  type EntExpenseState,
  type EntExpenseViolation,
} from '@/utils/approval/entExpense';
import type { ApprovalFormValues } from '../types';
import useAiUploadAttachments from '../useAiUploadAttachments';
import DetailTable from './DetailTable';
import { AiNoMatchNotice } from './styles';

const AI_DRAFT_SOURCE_URL = getApprovalAiDraftSourceUrl(ENT_EXPENSE_TYPE);

const buildFiles = (uploaded: AttachmentUploadResult, file: File) => [
  { fileUuid: uploaded.uuid, mimeType: file.type },
];

const ENT_BUDGET_ITEM_FIELD_ID = 'ent-expense-budget-item';

/** ENT. 프로젝트(비용) 품의(213) 본문 위 폼 */
const EntExpenseField = () => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { watch, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = watch('entExpense') ?? createEntExpenseState();
  const [costCenters, setCostCenters] = useState<EntCostCenterOption[] | null>(
    null,
  );
  const [aiNoMatch, setAiNoMatch] = useState(false);
  const budgetRequestRef = useRef(0);

  const update = useCallback(
    (fn: (prev: EntExpenseState) => EntExpenseState, dirty = true) => {
      const prev = getValues('entExpense') ?? createEntExpenseState();
      setValue('entExpense', fn(prev), { shouldDirty: dirty });
    },
    [getValues, setValue],
  );

  useEffect(() => {
    let alive = true;
    fetchApi<{ data?: unknown }>({
      method: 'GET',
      url: getSapCostCenterUrl(ENT_SAP_COMPANY_CODE),
    })
      .then((res) => {
        if (alive) setCostCenters(toCostCenterOptions(res?.data));
      })
      .catch(() => {
        if (!alive) return;
        snackbar.showError({
          message: t('사용 예산 목록을 불러오지 못했습니다.'),
        });
      });
    return () => {
      alive = false;
    };
  }, []);

  // 목록 조회에 성공했는데 편집 복원값이 없을 때만 지급 정보까지 비움(조회 실패는 null 유지)
  useEffect(() => {
    if (!costCenters || !state.budgetItem) return;
    if (costCenters.some((o) => o.value === state.budgetItem)) return;
    update(
      () => ({ ...createEntExpenseState(), budgetYear: state.budgetYear }),
      false,
    );
  }, [costCenters, state.budgetItem, state.budgetYear, update]);

  useEffect(() => {
    const { budgetItem, budgetYear } = state;
    if (!budgetItem) return;
    budgetRequestRef.current += 1;
    const request = budgetRequestRef.current;
    const query = new URLSearchParams({
      costCenter: budgetItem,
      year: budgetYear,
      season: ENT_BUDGET_SEASON,
    });
    const fail = () => {
      update((prev) => ({ ...prev, budget: null }), false);
      snackbar.showError({
        message: t('예산 정보를 불러오는데 실패했습니다.'),
      });
    };
    fetchApi<{ data?: unknown }>({
      method: 'GET',
      url: `${SAP_ENTERTAINMENT_BUDGET_URL}?${query.toString()}`,
    })
      .then((res) => {
        if (request !== budgetRequestRef.current) return;
        const budget = toEntBudget(res?.data);
        if (!budget) {
          fail();
          return;
        }
        update(
          (prev) => ({
            ...prev,
            budget,
            rows: syncRowsToBudget(prev.rows, budget),
          }),
          false,
        );
      })
      .catch(() => {
        if (request === budgetRequestRef.current) fail();
      });
  }, [state.budgetItem, state.budgetYear]);

  const handleBudgetItemChange = (value: string) => {
    setAiNoMatch(false);
    update((prev) => ({
      ...prev,
      budgetItem: value,
      budgetItemLabel: costCenters?.find((o) => o.value === value)?.label ?? '',
      budget: null,
      rows: prev.rows.map((r) => ({
        ...r,
        category: '',
        categoryLabel: '',
        item: '',
      })),
    }));
  };

  const handleYearChange = (year: string) =>
    update((prev) => ({
      ...prev,
      budgetYear: year,
      rows: prev.budgetItem
        ? prev.rows.map((r) => ({ ...r, item: '' }))
        : prev.rows,
    }));

  const { onUploadStart, onUploadSettled, onUploaded } =
    useAiUploadAttachments();

  const handleAiResult = useCallback(
    (results: Parameters<typeof applyEntAiResult>[0]) => {
      const current = getValues('entExpense') ?? createEntExpenseState();
      const applied = applyEntAiResult(results, current.budget);
      if (!applied) return;
      update((prev) => ({ ...prev, rows: applied.rows }));
      setAiNoMatch(!applied.matched);
    },
    [getValues, update],
  );

  const budgetOptions = costCenters ?? [];
  const budgetItemOptions =
    state.budgetItem && !budgetOptions.some((o) => o.value === state.budgetItem)
      ? [
          ...budgetOptions,
          { value: state.budgetItem, label: state.budgetItemLabel },
        ]
      : budgetOptions;

  return (
    <Box className="ent-expense" sx={{ mb: `${APPROVAL_SECTION_GAP}px` }}>
      <Box>
        <InfoSectionTitle sx={{ mb: 1 }}>{t('사용 예산')}</InfoSectionTitle>
        <BudgetSummaryTable
          editable
          budgetItem={
            <Box id={ENT_BUDGET_ITEM_FIELD_ID}>
              <Select
                value={state.budgetItem}
                placeholder={t('사용 예산을 선택해 주세요.')}
                options={budgetItemOptions}
                onChange={(e) => handleBudgetItemChange(String(e.target.value))}
                sx={approvalTableCellSelectSx}
              />
            </Box>
          }
          budgetYear={
            <Select
              value={state.budgetYear}
              options={getBudgetYearOptions().map((y) => ({
                value: y,
                label: y,
              }))}
              onChange={(e) => handleYearChange(String(e.target.value))}
              sx={approvalTableCellSelectSx}
            />
          }
          total={state.budget?.total ?? 0}
          remaining={state.budget?.remaining ?? 0}
          usageRate={state.budget?.usageRate ?? 0}
        />
      </Box>

      {state.budgetItem && (
        <>
          <Box sx={{ mt: `${APPROVAL_SECTION_GAP}px` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: '5px' }}>
              <InfoSectionTitle>{t('지급 정보')}</InfoSectionTitle>
              <Caption component="span" sx={{ ml: '10px' }}>
                {t('견적서를 업로드 하면 AI가 자동으로 항목을 채워드립니다.')}
              </Caption>
            </Box>
            <AiFileUploader
              requestUrl={AI_DRAFT_SOURCE_URL}
              getResultUrl={(id) => `${AI_DRAFT_SOURCE_URL}/${id}`}
              buildFiles={buildFiles}
              documentType={ENT_EXPENSE_TYPE}
              parseResult={parseEntAiResult}
              onUploadStart={onUploadStart}
              onUploadSettled={onUploadSettled}
              onUploaded={onUploaded}
              onResult={handleAiResult}
            />
          </Box>

          <Box sx={{ mt: `${APPROVAL_SECTION_GAP_LG}px` }}>
            {aiNoMatch && (
              <AiNoMatchNotice>
                <ExclamationCircleIcon />
                <Caption component="span" sx={{ color: 'inherit' }}>
                  {t(
                    '견적서에서 적절한 분류를 확인할 수 없습니다. 직접 선택해 주세요.',
                  )}
                </Caption>
              </AiNoMatchNotice>
            )}
            <DetailTable
              rows={state.rows}
              categories={state.budget?.categories ?? []}
              onChange={(rows) => update((prev) => ({ ...prev, rows }))}
              onCategoryPick={() => setAiNoMatch(false)}
            />
          </Box>

          <Box sx={{ mt: `${APPROVAL_SECTION_GAP}px` }}>
            <InfoSectionTitle sx={{ mb: 1 }}>
              {t('금번 품의 사용 예정')}
            </InfoSectionTitle>
            <TotalAmountTable
              editMode
              rows={computeEntTotalAmount(state.rows, state.budget)}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default EntExpenseField;

export const focusEntExpenseViolation = (violation: EntExpenseViolation) => {
  const scope = violation.rowId
    ? document.querySelector(
        `[data-row-id="${violation.rowId}"] [data-field="${violation.field}"]`,
      )
    : document.getElementById(ENT_BUDGET_ITEM_FIELD_ID);
  scope?.querySelector<HTMLElement>('[role="combobox"]')?.focus();
};
